/**
 * Pipe material specifications for the Pipe Sketch tool.
 *
 * Covers carbon steel (Sch 40 / Sch 80 butt weld), stainless (10S / 40S butt weld),
 * copper Type L (solder) and PVC Sch 40 (solvent weld). Every material carries the
 * fitting take-outs needed to turn a center-line sketch into real cut lengths.
 *
 * This file is the single place new materials / schedules get added.
 */

import {
  BW_45_C,
  BLIND_FLANGE_LB,
  VALVE_WEIGHT_LB,
  WN_FLANGE_LB,
  bySize,
} from "@/lib/material-data";

export type MaterialId =
  | "cs-a106-sch40"
  | "cs-a106-sch80"
  | "ss-304-sch10s"
  | "ss-304-sch40s"
  | "cu-type-l"
  | "pvc-sch40";

/** How pipe joins fittings — drives the ISO joint symbol. */
export type JointType = "bw" | "solder" | "solvent";

export interface PipeSizeSpec {
  /** Nominal pipe size label, e.g. `2"` */
  nps: string;
  /** Nominal size in inches (numeric, for math) */
  nom: number;
  /** Outside diameter, inches */
  od: number;
  /** Wall thickness, inches */
  wall: number;
  /** Weight, pounds per foot (plain end) */
  lbPerFt: number;
  /** 90° elbow center-to-end, inches */
  elbow90: number;
  /** 45° elbow center-to-end, inches */
  elbow45: number;
  /** Tee center-to-end on the run, inches */
  teeRun: number;
  /** Tee center-to-end on the branch, inches */
  teeBranch: number;
}

export interface MaterialSpec {
  id: MaterialId;
  label: string;
  shortLabel: string;
  spec: string;
  fittingSpec: string;
  joint: string;
  jointType: JointType;
  /** short note stamped on the sheet, e.g. `CS A106 SCH 40` */
  sheetTag: string;
  sizes: PipeSizeSpec[];
}

/** Steel weight, lb/ft, from OD and wall (10.69 factor), scaled for alloy density. */
const steelWeight = (od: number, wall: number, factor = 1) =>
  +(10.69 * (od - wall) * wall * factor).toFixed(2);

/** Standard steel pipe OD by nominal size. */
const STEEL_OD: Record<number, number> = {
  0.5: 0.84,
  0.75: 1.05,
  1: 1.315,
  1.25: 1.66,
  1.5: 1.9,
  2: 2.375,
  2.5: 2.875,
  3: 3.5,
  4: 4.5,
  5: 5.563,
  6: 6.625,
  8: 8.625,
  10: 10.75,
  12: 12.75,
};

const NPS_LABEL: Record<number, string> = {
  0.5: '1/2"',
  0.75: '3/4"',
  1: '1"',
  1.25: '1-1/4"',
  1.5: '1-1/2"',
  2: '2"',
  2.5: '2-1/2"',
  3: '3"',
  4: '4"',
  5: '5"',
  6: '6"',
  8: '8"',
  10: '10"',
  12: '12"',
};

/** ASME B16.9 butt-weld tee center-to-end ("C"), inches. */
const BW_TEE_C: Record<number, number> = {
  0.5: 1.0,
  0.75: 1.12,
  1: 1.5,
  1.25: 1.88,
  1.5: 2.25,
  2: 2.5,
  2.5: 3.0,
  3: 3.38,
  4: 4.12,
  5: 4.88,
  6: 5.62,
  8: 7.0,
  10: 8.5,
  12: 10.0,
};

/** Butt-weld (long radius) steel size row. */
const bwSize = (nom: number, wall: number, densityFactor = 1): PipeSizeSpec => {
  const od = STEEL_OD[nom];
  const teeC = BW_TEE_C[nom];
  return {
    nps: NPS_LABEL[nom],
    nom,
    od,
    wall,
    lbPerFt: steelWeight(od, wall, densityFactor),
    elbow90: +(1.5 * nom).toFixed(3),
    elbow45: BW_45_C[nom] ?? +(0.625 * nom).toFixed(3),
    teeRun: teeC,
    teeBranch: teeC,
  };
};

const SCH40_WALL: Record<number, number> = {
  0.5: 0.109,
  0.75: 0.113,
  1: 0.133,
  1.25: 0.14,
  1.5: 0.145,
  2: 0.154,
  2.5: 0.203,
  3: 0.216,
  4: 0.237,
  5: 0.258,
  6: 0.28,
  8: 0.322,
  10: 0.365,
  12: 0.406,
};

const SCH80_WALL: Record<number, number> = {
  0.5: 0.147,
  0.75: 0.154,
  1: 0.179,
  1.25: 0.191,
  1.5: 0.2,
  2: 0.218,
  2.5: 0.276,
  3: 0.3,
  4: 0.337,
  5: 0.375,
  6: 0.432,
  8: 0.5,
  10: 0.594,
  12: 0.688,
};

const SCH10S_WALL: Record<number, number> = {
  0.5: 0.083,
  0.75: 0.083,
  1: 0.109,
  1.25: 0.109,
  1.5: 0.109,
  2: 0.109,
  2.5: 0.12,
  3: 0.12,
  4: 0.12,
  6: 0.134,
  8: 0.148,
  10: 0.165,
  12: 0.18,
};

const rows = (walls: Record<number, number>, densityFactor = 1) =>
  Object.keys(walls)
    .map(Number)
    .sort((a, b) => a - b)
    .map((n) => bwSize(n, walls[n], densityFactor));

export const CS_SCH40: MaterialSpec = {
  id: "cs-a106-sch40",
  label: "Carbon Steel — A106 Gr.B, Sch 40",
  shortLabel: "CS Sch 40",
  spec: "ASTM A106 Gr.B SMLS",
  fittingSpec: "ASME B16.9 BW LR",
  joint: "Butt Weld",
  jointType: "bw",
  sheetTag: "CS A106 SCH 40",
  sizes: rows(SCH40_WALL),
};

export const CS_SCH80: MaterialSpec = {
  id: "cs-a106-sch80",
  label: "Carbon Steel — A106 Gr.B, Sch 80",
  shortLabel: "CS Sch 80",
  spec: "ASTM A106 Gr.B SMLS",
  fittingSpec: "ASME B16.9 BW LR",
  joint: "Butt Weld",
  jointType: "bw",
  sheetTag: "CS A106 SCH 80",
  sizes: rows(SCH80_WALL),
};

export const SS_SCH10S: MaterialSpec = {
  id: "ss-304-sch10s",
  label: "Stainless 304/304L — Sch 10S",
  shortLabel: "SS 10S",
  spec: "ASTM A312 TP304L",
  fittingSpec: "ASME B16.9 BW LR",
  joint: "Butt Weld",
  jointType: "bw",
  sheetTag: "SS 304L SCH 10S",
  sizes: rows(SCH10S_WALL, 1.02),
};

export const SS_SCH40S: MaterialSpec = {
  id: "ss-304-sch40s",
  label: "Stainless 304/304L — Sch 40S",
  shortLabel: "SS 40S",
  spec: "ASTM A312 TP304L",
  fittingSpec: "ASME B16.9 BW LR",
  joint: "Butt Weld",
  jointType: "bw",
  sheetTag: "SS 304L SCH 40S",
  sizes: rows(SCH40_WALL, 1.02),
};

/** Copper Type L, wrought solder fittings (ASME B16.22). */
const CU: Array<[number, number, number, number, number]> = [
  // nom, wall, lb/ft, 90 c-to-e, tee c-to-e
  [0.5, 0.04, 0.285, 0.62, 0.62],
  [0.75, 0.045, 0.455, 0.75, 0.75],
  [1, 0.05, 0.655, 0.88, 0.88],
  [1.25, 0.055, 0.884, 1.0, 1.0],
  [1.5, 0.06, 1.14, 1.13, 1.13],
  [2, 0.07, 1.75, 1.38, 1.38],
  [2.5, 0.08, 2.48, 1.75, 1.75],
  [3, 0.09, 3.33, 2.0, 2.0],
  [4, 0.11, 5.38, 2.5, 2.5],
];

export const CU_TYPE_L: MaterialSpec = {
  id: "cu-type-l",
  label: "Copper — Type L, solder",
  shortLabel: "Cu Type L",
  spec: "ASTM B88 Type L",
  fittingSpec: "ASME B16.22 wrought",
  joint: "Solder",
  jointType: "solder",
  sheetTag: "CU TYPE L SOLDER",
  sizes: CU.map(([nom, wall, lb, e90, teeC]) => ({
    nps: NPS_LABEL[nom],
    nom,
    od: +(nom + 0.125).toFixed(3),
    wall,
    lbPerFt: lb,
    elbow90: e90,
    elbow45: +(e90 * 0.45).toFixed(2),
    teeRun: teeC,
    teeBranch: teeC,
  })),
};

/** PVC Sch 40, socket (solvent weld) fittings. */
const PVC: Array<[number, number, number, number]> = [
  // nom, wall, lb/ft, 90 c-to-e (socket)
  [0.5, 0.109, 0.16, 0.88],
  [0.75, 0.113, 0.21, 1.0],
  [1, 0.133, 0.32, 1.19],
  [1.25, 0.14, 0.43, 1.38],
  [1.5, 0.145, 0.52, 1.5],
  [2, 0.154, 0.7, 1.81],
  [2.5, 0.203, 1.1, 2.19],
  [3, 0.216, 1.49, 2.5],
  [4, 0.237, 2.12, 3.19],
  [6, 0.28, 3.79, 4.5],
  [8, 0.322, 6.41, 5.75],
];

export const PVC_SCH40: MaterialSpec = {
  id: "pvc-sch40",
  label: "PVC — Sch 40, solvent weld",
  shortLabel: "PVC Sch 40",
  spec: "ASTM D1785 Sch 40",
  fittingSpec: "ASTM D2466 socket",
  joint: "Solvent Weld",
  jointType: "solvent",
  sheetTag: "PVC SCH 40 SOLVENT",
  sizes: PVC.map(([nom, wall, lb, e90]) => ({
    nps: NPS_LABEL[nom],
    nom,
    od: STEEL_OD[nom],
    wall,
    lbPerFt: lb,
    elbow90: e90,
    elbow45: +(e90 * 0.6).toFixed(2),
    teeRun: e90,
    teeBranch: e90,
  })),
};

export const MATERIALS: Record<MaterialId, MaterialSpec> = {
  "cs-a106-sch40": CS_SCH40,
  "cs-a106-sch80": CS_SCH80,
  "ss-304-sch10s": SS_SCH10S,
  "ss-304-sch40s": SS_SCH40S,
  "cu-type-l": CU_TYPE_L,
  "pvc-sch40": PVC_SCH40,
};

export const DEFAULT_MATERIAL: MaterialId = "cs-a106-sch40";
export const DEFAULT_SIZE = '2"';

export function getMaterial(material: MaterialId): MaterialSpec {
  return MATERIALS[material] ?? CS_SCH40;
}

export function getSizeSpec(material: MaterialId, nps: string): PipeSizeSpec {
  const m = getMaterial(material);
  return m.sizes.find((s) => s.nps === nps) ?? m.sizes[0];
}

/** Nearest available size in a material (used when switching materials). */
export function nearestSize(material: MaterialId, nps: string): string {
  const m = getMaterial(material);
  const exact = m.sizes.find((s) => s.nps === nps);
  if (exact) return exact.nps;
  const target = Object.entries(NPS_LABEL).find(([, l]) => l === nps)?.[0];
  const nom = target ? Number(target) : 2;
  return m.sizes.reduce((best, s) =>
    Math.abs(s.nom - nom) < Math.abs(best.nom - nom) ? s : best,
  ).nps;
}

/**
 * Concentric butt-weld reducer end-to-end length (ASME B16.9 "H"),
 * keyed by the LARGER nominal size, inches.
 */
const REDUCER_H: Record<number, number> = {
  0.75: 1.5,
  1: 2,
  1.25: 2,
  1.5: 2.5,
  2: 3,
  2.5: 3.5,
  3: 3.5,
  4: 4,
  5: 5,
  6: 5.5,
  8: 6,
  10: 7,
  12: 8,
};

export interface ReducerInfo {
  large: PipeSizeSpec;
  small: PipeSizeSpec;
  /** overall end-to-end length, inches */
  length: number;
  label: string;
}

/** Concentric reducer between two sizes. Returns null if the sizes match. */
export function getReducer(
  material: MaterialId,
  a: string,
  b: string,
): ReducerInfo | null {
  if (a === b) return null;
  const m = getMaterial(material);
  const sa = getSizeSpec(material, a);
  const sb = getSizeSpec(material, b);
  const large = sa.nom >= sb.nom ? sa : sb;
  const small = sa.nom >= sb.nom ? sb : sa;
  const bw = REDUCER_H[large.nom] ?? +(large.nom * 1.0).toFixed(2);
  // socket / solder reducing couplings are much shorter than BW reducers
  const length = m.jointType === "bw" ? bw : +(large.nom * 0.9 + 0.5).toFixed(2);
  const kind =
    m.jointType === "bw" ? "Conc Reducer BW" : m.jointType === "solder" ? "Reducer Cplg" : "Reducer Bushing";
  return {
    large,
    small,
    length,
    label: `${large.nps} × ${small.nps} ${kind}`,
  };
}

/* ------------------------------------------------------------------ *
 * Valves, flanges and other in-line components
 * ------------------------------------------------------------------ */

export type ComponentKind =
  | "gate"
  | "ball"
  | "globe"
  | "check"
  | "butterfly"
  | "flangePair"
  | "blindFlange"
  | "union"
  | "strainer";

/** ASME B16.10 class 150 flanged face-to-face, inches. */
const FF_GATE: Record<number, number> = {
  0.5: 4.25,
  0.75: 4.62,
  1: 5.0,
  1.25: 5.5,
  1.5: 6.5,
  2: 7.0,
  2.5: 7.5,
  3: 8.0,
  4: 9.0,
  5: 10.0,
  6: 10.5,
  8: 11.5,
  10: 13.0,
  12: 14.0,
};

const FF_CHECK: Record<number, number> = {
  0.5: 4.25,
  0.75: 4.62,
  1: 5.0,
  1.25: 5.5,
  1.5: 6.5,
  2: 8.0,
  2.5: 8.5,
  3: 9.5,
  4: 11.5,
  5: 13.0,
  6: 14.0,
  8: 19.5,
  10: 24.5,
  12: 27.5,
};

const FF_BUTTERFLY: Record<number, number> = {
  2: 1.69,
  2.5: 1.81,
  3: 1.81,
  4: 2.06,
  5: 2.19,
  6: 2.19,
  8: 2.38,
  10: 2.68,
  12: 3.06,
};

/** Weld-neck flange length through hub (face to weld end), inches. */
const WN_FLANGE_LEN: Record<number, number> = {
  0.5: 1.88,
  0.75: 2.06,
  1: 2.19,
  1.25: 2.25,
  1.5: 2.44,
  2: 2.5,
  2.5: 2.75,
  3: 2.75,
  4: 3.0,
  5: 3.5,
  6: 3.5,
  8: 4.0,
  10: 4.0,
  12: 4.5,
};

export interface ComponentSpec {
  kind: ComponentKind;
  label: string;
  short: string;
  /** total centerline length consumed, inches */
  length: number;
  /** face-to-face of the body itself, inches */
  bodyLength: number;
  /** flange length per end (0 when not flanged) */
  flangeLength: number;
  /** number of pipe joints (welds / solder joints) it adds */
  joints: number;
  /** approximate installed weight, lb */
  weight: number;
  /** takeoff description for the cut list */
  note: string;
}

const COMPONENT_META: Record<
  ComponentKind,
  { label: string; short: string; table?: Record<number, number>; joints: number; flanged: boolean }
> = {
  gate: { label: "Gate Valve", short: "GATE", table: FF_GATE, joints: 2, flanged: true },
  ball: { label: "Ball Valve", short: "BALL", table: FF_GATE, joints: 2, flanged: true },
  globe: { label: "Globe Valve", short: "GLOBE", table: FF_GATE, joints: 2, flanged: true },
  check: { label: "Check Valve", short: "CHK", table: FF_CHECK, joints: 2, flanged: true },
  butterfly: {
    label: "Butterfly Valve (wafer)",
    short: "BFLY",
    table: FF_BUTTERFLY,
    joints: 2,
    flanged: true,
  },
  flangePair: { label: "Flange Pair (WN, 150#)", short: "FLG", joints: 2, flanged: true },
  blindFlange: { label: "Blind Flange (150#)", short: "BLND", joints: 1, flanged: true },
  union: { label: "Union", short: "UN", joints: 2, flanged: false },
  strainer: { label: "Y-Strainer", short: "STR", table: FF_CHECK, joints: 2, flanged: true },
};

export const COMPONENT_KINDS = Object.keys(COMPONENT_META) as ComponentKind[];

export function componentLabel(kind: ComponentKind) {
  return COMPONENT_META[kind].label;
}

/** Full take-off data for a component at a given size. */
export function getComponentSpec(
  material: MaterialId,
  kind: ComponentKind,
  nps: string,
): ComponentSpec {
  const meta = COMPONENT_META[kind];
  const s = getSizeSpec(material, nps);
  const m = getMaterial(material);
  const soft = m.jointType !== "bw";

  const flangeLength = meta.flanged && !soft ? (WN_FLANGE_LEN[s.nom] ?? s.nom * 0.5) : 0;
  let bodyLength: number;
  if (kind === "flangePair") bodyLength = 0.125; // gasket
  else if (kind === "blindFlange") bodyLength = 0;
  else if (kind === "union") bodyLength = +(s.nom * 0.9 + 1.2).toFixed(2);
  else bodyLength = meta.table?.[s.nom] ?? +(s.nom * 2 + 3).toFixed(2);

  // soldered / solvent valves are compact bodies with no flanges
  if (soft && kind !== "union") bodyLength = +(bodyLength * 0.55).toFixed(2);

  const ends = kind === "blindFlange" ? 1 : 2;
  const length = +(bodyLength + flangeLength * (meta.flanged && !soft ? ends : 0)).toFixed(2);
  // real product weights: B16.5 flanges, published valve body weights
  const weight =
    kind === "flangePair"
      ? +(bySize(WN_FLANGE_LB, s.nom, s.nom * s.nom * 1.6) * 2).toFixed(1)
      : kind === "blindFlange"
        ? +bySize(BLIND_FLANGE_LB, s.nom, s.nom * s.nom * 1.6).toFixed(1)
        : +(
            bySize(VALVE_WEIGHT_LB[kind], s.nom, s.nom * s.nom * 3.2) *
            (soft ? 0.45 : 1)
          ).toFixed(1);

  const jointName = m.jointType === "bw" ? "BW" : m.jointType === "solder" ? "Solder" : "Solvent";
  const note =
    kind === "flangePair"
      ? `${nps} 150# WN flange pair + gasket`
      : kind === "blindFlange"
        ? `${nps} 150# blind flange`
        : soft
          ? `${nps} ${meta.label} (${jointName})`
          : `${nps} ${meta.label} 150# flanged`;

  return {
    kind,
    label: `${nps} ${meta.label}`,
    short: meta.short,
    length,
    bodyLength,
    flangeLength,
    joints: soft ? meta.joints : meta.joints,
    weight,
    note,
  };
}

/** Format inches as feet-inches, e.g. 27.5 -> 2'-3 1/2" */
export function formatFtIn(totalInches: number): string {
  const neg = totalInches < 0;
  const v = Math.abs(totalInches);
  const ft = Math.floor(v / 12);
  const inches = v - ft * 12;
  const whole = Math.floor(inches + 1e-6);
  const frac = inches - whole;
  const sixteenths = Math.round(frac * 16);
  let fracStr = "";
  let w = whole;
  if (sixteenths === 16) {
    w += 1;
  } else if (sixteenths > 0) {
    let n = sixteenths;
    let d = 16;
    while (n % 2 === 0) {
      n /= 2;
      d /= 2;
    }
    fracStr = ` ${n}/${d}`;
  }
  const parts: string[] = [];
  if (ft > 0) parts.push(`${ft}'`);
  if (w > 0 || fracStr || ft === 0) parts.push(`${w}${fracStr}"`);
  return (neg ? "-" : "") + parts.join("-");
}

/* ------------------------------------------------------------------ *
 * Supports & hangers (MSS SP-58 / ASME B31.1 Table 121.5)
 * ------------------------------------------------------------------ */

export type SupportKind =
  | "hanger"
  | "trapeze"
  | "guide"
  | "anchor"
  | "spring"
  | "shoe"
  | "roller"
  | "riserClamp";

const SUPPORT_META: Record<
  SupportKind,
  { label: string; short: string; detail: string; base: number; rated: boolean }
> = {
  hanger: {
    label: "Clevis Hanger",
    short: "HGR",
    detail: "MSS SP-58 Type 1 clevis + rod",
    base: 1.2,
    rated: true,
  },
  trapeze: {
    label: "Trapeze Hanger",
    short: "TRAP",
    detail: "Strut trapeze, two rods",
    base: 3.5,
    rated: true,
  },
  guide: {
    label: "Pipe Guide",
    short: "GUIDE",
    detail: "MSS SP-58 Type 35 guide, axial travel only",
    base: 2.4,
    rated: true,
  },
  anchor: {
    label: "Anchor",
    short: "ANCH",
    detail: "Welded anchor — no movement in any direction",
    base: 6,
    rated: false,
  },
  spring: {
    label: "Variable Spring Hanger",
    short: "SPRG",
    detail: "MSS SP-58 Type 51 variable spring",
    base: 14,
    rated: true,
  },
  shoe: {
    label: "Pipe Shoe",
    short: "SHOE",
    detail: "Welded shoe on steel / concrete",
    base: 4,
    rated: true,
  },
  roller: {
    label: "Roller Support",
    short: "ROLL",
    detail: "MSS SP-58 Type 41 adjustable roller",
    base: 5,
    rated: true,
  },
  riserClamp: {
    label: "Riser Clamp",
    short: "RSCL",
    detail: "MSS SP-58 Type 8 riser clamp (vertical only)",
    base: 3,
    rated: false,
  },
};

export const SUPPORT_KINDS = Object.keys(SUPPORT_META) as SupportKind[];

export function supportLabel(kind: SupportKind) {
  return SUPPORT_META[kind].label;
}

/** Maximum horizontal support spacing, feet — steel water service (B31.1 121.5). */
const SPAN_STEEL: Record<number, number> = {
  0.5: 7,
  0.75: 7,
  1: 7,
  1.25: 7,
  1.5: 9,
  2: 10,
  2.5: 11,
  3: 12,
  4: 14,
  5: 16,
  6: 17,
  8: 19,
  10: 22,
  12: 23,
};

/** Copper tube support spacing, feet (IPC Table 308.5). */
const SPAN_COPPER: Record<number, number> = {
  0.5: 6,
  0.75: 6,
  1: 6,
  1.25: 6,
  1.5: 10,
  2: 10,
  2.5: 10,
  3: 10,
  4: 10,
};

/** Max recommended support spacing for a size, in feet. */
export function maxSpanFt(material: MaterialId, nps: string): number {
  const s = getSizeSpec(material, nps);
  const m = getMaterial(material);
  if (m.jointType === "solder") return SPAN_COPPER[s.nom] ?? 6;
  // PVC creeps — roughly half the steel span at ambient
  if (m.jointType === "solvent") return Math.max(3, Math.round((SPAN_STEEL[s.nom] ?? 10) * 0.45));
  return SPAN_STEEL[s.nom] ?? 10;
}

export interface SupportSpec {
  kind: SupportKind;
  label: string;
  short: string;
  detail: string;
  /** approximate installed hardware weight, lb */
  weight: number;
  /** rod / strut size callout */
  rodSize: string;
  /** schedule line for the takeoff */
  note: string;
  /** max spacing this support type may be set at, feet (0 = n/a) */
  maxSpanFt: number;
}

/** Hanger rod diameter by pipe size (MSS SP-58 Table 3). */
function rodSize(nom: number): string {
  if (nom <= 2) return '3/8" rod';
  if (nom <= 3) return '1/2" rod';
  if (nom <= 5) return '5/8" rod';
  if (nom <= 8) return '3/4" rod';
  if (nom <= 12) return '7/8" rod';
  return '1" rod';
}

export function getSupportSpec(
  material: MaterialId,
  kind: SupportKind,
  nps: string,
): SupportSpec {
  const meta = SUPPORT_META[kind];
  const s = getSizeSpec(material, nps);
  const rod = kind === "trapeze" ? `(2) ${rodSize(s.nom)} + strut` : rodSize(s.nom);
  const weight = +(meta.base * (1 + s.nom * 0.35)).toFixed(1);
  return {
    kind,
    label: `${nps} ${meta.label}`,
    short: meta.short,
    detail: meta.detail,
    weight,
    rodSize: rod,
    note: `${nps} ${meta.label} — ${meta.detail}`,
    maxSpanFt: meta.rated ? maxSpanFt(material, nps) : 0,
  };
}

/* ------------------------------------------------------------------ *
 * Equipment connections & line terminations
 * ------------------------------------------------------------------ */

export type TerminationKind =
  | "cap"
  | "flangedEnd"
  | "blindEnd"
  | "pumpNozzle"
  | "vesselNozzle"
  | "exchangerNozzle"
  | "tankNozzle"
  | "equipment"
  | "openEnd"
  | "futureConn";

const TERMINATION_META: Record<
  TerminationKind,
  { label: string; short: string; detail: string; joints: number; equipment: boolean }
> = {
  cap: {
    label: "Pipe Cap",
    short: "CAP",
    detail: "ASME B16.9 cap, welded on",
    joints: 1,
    equipment: false,
  },
  flangedEnd: {
    label: "Flanged End (WN 150#)",
    short: "FLG",
    detail: "Weld neck flange, gasket + bolt set",
    joints: 1,
    equipment: false,
  },
  blindEnd: {
    label: "Blind Flange End",
    short: "BLND",
    detail: "WN flange + blind, future tie-in",
    joints: 1,
    equipment: false,
  },
  pumpNozzle: {
    label: "Pump Nozzle",
    short: "PUMP",
    detail: "Flanged pump connection",
    joints: 1,
    equipment: true,
  },
  vesselNozzle: {
    label: "Vessel Nozzle",
    short: "VESS",
    detail: "Flanged vessel / tower nozzle",
    joints: 1,
    equipment: true,
  },
  exchangerNozzle: {
    label: "Exchanger Nozzle",
    short: "EXCH",
    detail: "Flanged heat exchanger nozzle",
    joints: 1,
    equipment: true,
  },
  tankNozzle: {
    label: "Tank Nozzle",
    short: "TANK",
    detail: "Flanged tank nozzle",
    joints: 1,
    equipment: true,
  },
  equipment: {
    label: "Equipment Connection",
    short: "EQPT",
    detail: "Generic flanged equipment tie-in",
    joints: 1,
    equipment: true,
  },
  openEnd: {
    label: "Open End",
    short: "OPEN",
    detail: "Plain end — field cut to suit",
    joints: 0,
    equipment: false,
  },
  futureConn: {
    label: "Future Connection",
    short: "FUT",
    detail: "Capped for future extension",
    joints: 1,
    equipment: false,
  },
};

export const TERMINATION_KINDS = Object.keys(TERMINATION_META) as TerminationKind[];

export function terminationLabel(kind: TerminationKind) {
  return TERMINATION_META[kind].label;
}

/** ASME B16.9 butt-weld cap length "E", inches. */
const CAP_E: Record<number, number> = {
  0.5: 1,
  0.75: 1,
  1: 1.5,
  1.25: 1.5,
  1.5: 1.5,
  2: 1.5,
  2.5: 1.5,
  3: 2,
  4: 2.5,
  5: 3,
  6: 3.5,
  8: 4,
  10: 5,
  12: 6,
};

export interface TerminationSpec {
  kind: TerminationKind;
  label: string;
  short: string;
  detail: string;
  /** centerline length consumed at the end of the run, inches */
  takeoff: number;
  joints: number;
  weight: number;
  /** true when the symbol should draw an equipment body */
  equipment: boolean;
  note: string;
}

export function getTerminationSpec(
  material: MaterialId,
  kind: TerminationKind,
  nps: string,
): TerminationSpec {
  const meta = TERMINATION_META[kind];
  const s = getSizeSpec(material, nps);
  const m = getMaterial(material);
  const soft = m.jointType !== "bw";
  const flange = soft ? +(s.nom * 0.5 + 0.5).toFixed(2) : (WN_FLANGE_LEN[s.nom] ?? s.nom * 0.5);

  let takeoff: number;
  if (kind === "openEnd") takeoff = 0;
  else if (kind === "cap" || kind === "futureConn") takeoff = CAP_E[s.nom] ?? +(s.nom * 0.75).toFixed(2);
  else takeoff = flange;

  const weight =
    kind === "openEnd"
      ? 0
      : +(s.nom * s.nom * (meta.equipment || kind === "blindEnd" ? 1.8 : 1.1) + 2).toFixed(1);

  return {
    kind,
    label: `${nps} ${meta.label}`,
    short: meta.short,
    detail: meta.detail,
    takeoff,
    joints: meta.joints,
    weight,
    equipment: meta.equipment,
    note: `${nps} ${meta.label} — ${meta.detail}`,
  };
}

/** Format an elevation in inches as EL 12'-6" (always signed feet-inches). */
export function formatElevation(inches: number): string {
  const sign = inches < 0 ? "-" : "+";
  return `EL ${sign}${formatFtIn(Math.abs(inches))}`;
}

/** Format a plan coordinate pair, e.g. `N 12'-0"  E 4'-6"`. */
export function formatCoords(x: number, y: number): string {
  const ns = y >= 0 ? `N ${formatFtIn(y)}` : `S ${formatFtIn(-y)}`;
  const ew = x >= 0 ? `E ${formatFtIn(x)}` : `W ${formatFtIn(-x)}`;
  return `${ns}  ${ew}`;
}
